import React, { useMemo, useState } from 'react';
import ProjectCard from '../ProjectCard';
import ProjectModal from '../ProjectModal';
import projects, { Project } from '../../data/projects';

interface Filter {
  id: string;
  label: string;
}

const filters: Filter[] = [
  { id: 'all', label: 'All' },
  { id: '3d', label: '3D and hardware' },
  { id: 'ai', label: 'AI and vision' },
  { id: 'game', label: 'Game development' },
  { id: 'web', label: 'Web and fullstack' },
  { id: 'automation', label: 'Automation' },
];

/** Project ids highlighted as primary work at the top of the grid. */
const featuredIds = ['talon', 'apexPayloadDropper', 'peltierSleepingMat', '3dpcbuilder'];

const sortProjects = (list: Project[]): Project[] => {
  const featured = featuredIds
    .map(id => list.find(project => project.id === id))
    .filter((project): project is Project => Boolean(project));

  const rest = list.filter(project => !featuredIds.includes(project.id));

  return [...featured, ...rest];
};

const Portfolio: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const filteredProjects = useMemo(() => {
    const matching =
      activeFilter === 'all'
        ? projects
        : projects.filter(project => project.category === activeFilter);

    return sortProjects(matching);
  }, [activeFilter]);

  return (
    <section className="work section" id="work">
      <div className="container">
        <div className="section-head">
          <p className="section-head__kicker">Selected work</p>
          <h2 className="section-head__title">Things I have made.</h2>
          <p className="section-head__lede">
            Software projects, small experiments, and 3D printed parts.
          </p>
        </div>

        <div className="work__controls">
          <div className="work__filters" role="group" aria-label="Filter projects">
            {filters.map(filter => (
              <button
                key={filter.id}
                type="button"
                aria-pressed={activeFilter === filter.id}
                className={`work__filter ${activeFilter === filter.id ? 'is-active' : ''}`}
                data-filter={filter.id}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <p className="work__count">
            {String(filteredProjects.length).padStart(2, '0')} projects
          </p>
        </div>

        <div className="work__grid">
          {filteredProjects.map(project => (
            <ProjectCard key={project.id} project={project} onClick={setActiveModal} />
          ))}
        </div>
      </div>

      {projects.map(project => (
        <ProjectModal
          key={project.id}
          project={project}
          isActive={activeModal === project.id}
          onClose={() => setActiveModal(null)}
        />
      ))}
    </section>
  );
};

export default Portfolio;